import type { HabitWithLog } from '../interfaces'
import { getWeeklyDaysLeft, isScheduledDay } from './dateHelpers'

export interface WeeklyNoticeInfo {
  isWeeklyHabit: boolean
  isTargetMet: boolean
  daysLeft: number
  needed: number
  isUrgent: boolean
  badgeText: string | null
  badgeClass: string
}

export function getWeeklyNoticeInfo(
  habit: HabitWithLog,
  selectedDate: Date,
  dayStartTime = '00:00',
): WeeklyNoticeInfo {
  const hasSubHabits = (habit.subHabits?.length ?? 0) > 0
  const isWeeklyHabit =
    habit.frequency_type === 'x_per_week' ||
    habit.frequency_type === 'weekly' ||
    (hasSubHabits && (habit.frequency_target ?? 0) > 0)

  if (!isWeeklyHabit) {
    return {
      isWeeklyHabit: false,
      isTargetMet: false,
      daysLeft: 0,
      needed: 0,
      isUrgent: false,
      badgeText: null,
      badgeClass: '',
    }
  }

  const isWeeklyType = habit.frequency_type === 'weekly'
  const target = isWeeklyType ? 1 : (habit.frequency_target ?? 1)
  const isTargetMet = isWeeklyType
    ? habit.isCompletedThisPeriod
    : (habit.isWeeklyTargetMet ?? habit.weeklyCompletions >= target)

  if (isTargetMet) {
    return {
      isWeeklyHabit: true,
      isTargetMet: true,
      daysLeft: 0,
      needed: 0,
      isUrgent: false,
      badgeText: null,
      badgeClass: '',
    }
  }

  const isCompletedToday = habit.isCompletedThisPeriod && habit.logId !== null
  const daysLeft = getWeeklyDaysLeft(
    selectedDate,
    dayStartTime,
    !isCompletedToday,
  )
  const needed = isWeeklyType
    ? 1
    : Math.max(0, target - habit.weeklyCompletions)

  const isUrgent = daysLeft <= needed || daysLeft <= 2
  const daysLabel = daysLeft === 1 ? '1 day left' : `${daysLeft} days left`

  let badgeText: string
  let badgeClass: string

  if (isUrgent) {
    if (daysLeft <= needed) {
      badgeText = `⚠️ ${daysLabel}`
      badgeClass = 'bg-rose-100 text-rose-700'
    } else {
      badgeText = `⏳ ${daysLabel}`
      badgeClass = 'bg-amber-100 text-amber-800'
    }
  } else {
    badgeText = `⏳ ${daysLabel}`
    badgeClass = 'bg-slate-100 text-slate-600'
  }

  return {
    isWeeklyHabit: true,
    isTargetMet: false,
    daysLeft,
    needed,
    isUrgent,
    badgeText,
    badgeClass,
  }
}

export interface HighlightResult {
  isScheduled: boolean
  isStreakAtRisk: boolean
  isEndOfWeekPush: boolean
  isRecovery: boolean
  isPinned: boolean
  isExtraImportant: boolean
  borderClass: string
  highlightReasonEmoji: string | null
}

export function getHabitHighlight(
  habit: HabitWithLog,
  selectedDate: Date,
  dayStartTime: string,
  options?: {
    isSubHabit?: boolean
    parentIsCompleted?: boolean
  },
): HighlightResult {
  const isCompleted = habit.isCompletedThisPeriod
  const isSkipped = habit.isSkippedThisPeriod

  let isScheduled = true
  if (habit.frequency_type === 'weekly_days') {
    isScheduled = isScheduledDay(selectedDate, habit.weekly_days, dayStartTime)
  }

  const isStreakAtRisk = isScheduled && habit.currentStreak >= 3

  const weeklyNotice = getWeeklyNoticeInfo(habit, selectedDate, dayStartTime)
  const isEndOfWeekPush = weeklyNotice.isUrgent

  const isRecovery =
    habit.frequency_type === 'daily' &&
    habit.bestStreak > 0 &&
    habit.currentStreak === 0

  const isPinned = !options?.isSubHabit && !!habit.pinned

  const isExtraImportant =
    isStreakAtRisk || isEndOfWeekPush || isRecovery || isPinned

  let borderClass = options?.isSubHabit ? 'border-gray-100' : 'border-gray-200'
  let highlightReasonEmoji: string | null = null

  if (!isCompleted && !isSkipped) {
    if (isExtraImportant) {
      borderClass = 'border-rose-400 ring-2 ring-rose-400 shadow-md'
      if (isStreakAtRisk) highlightReasonEmoji = '🔥'
      else if (isEndOfWeekPush)
        highlightReasonEmoji =
          weeklyNotice.daysLeft <= weeklyNotice.needed ? '⚠️' : '⏳'
      else if (isRecovery) highlightReasonEmoji = '❤️‍🩹'
      else if (isPinned) highlightReasonEmoji = '📌'
    } else if (
      isScheduled &&
      !(
        options?.isSubHabit &&
        habit.frequency_type === 'daily' &&
        options.parentIsCompleted
      )
    ) {
      borderClass = options?.isSubHabit
        ? 'border-indigo-400 ring-1 ring-indigo-400 shadow-sm'
        : 'border-indigo-400 ring-1 ring-indigo-400'
    }
  }

  return {
    isScheduled,
    isStreakAtRisk,
    isEndOfWeekPush,
    isRecovery,
    isPinned,
    isExtraImportant,
    borderClass,
    highlightReasonEmoji,
  }
}
